import { useEffect, useMemo } from "react";
import classNames from "clsx";
import { Tooltip } from "react-migration/components/Tooltip";
import { Theme } from "react-migration/lib/theme/Theme";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo/routedClient";
import { useSelectedParcelIds } from "react-migration/domains/sites/skipTracing/store/hooks/useSelectedParcelIds";
import { useSkipTracingCreditsQuery } from "@gql/li-api/graphql";
import { updateValidationError } from "react-migration/domains/sites/skipTracing/store/actions/updateValidationError";

export const ConfirmOrder = () => {
  const { t } = useTranslation();

  const selectedParcelIds = useSelectedParcelIds();

  const { data: creditsResponse } = useSkipTracingCreditsQuery({
    client: routedClient,
    fetchPolicy: "network-only",
    context: {
      endpoint: LandTechEndpoints.PrivateClient,
    },
  });

  const accountSkipTraceCredit = useMemo(
    () => ({
      cap: creditsResponse?.accountWithViews.skip_traces?.cap ?? 0,
      used: creditsResponse?.accountWithViews.skip_traces?.used ?? 0,
    }),
    [creditsResponse]
  );

  const selectedParcelCount = useMemo(() => Object.keys(selectedParcelIds).length ?? 0, [selectedParcelIds]);

  const remainingCredits = useMemo(() => {
    if (accountSkipTraceCredit?.used && accountSkipTraceCredit.cap) {
      const creditsAfterPurchase = accountSkipTraceCredit.cap - (accountSkipTraceCredit.used + selectedParcelCount);
      return `${creditsAfterPurchase > 0 ? creditsAfterPurchase : 0}/${accountSkipTraceCredit.cap}`;
    }
  }, [accountSkipTraceCredit.cap, accountSkipTraceCredit.used, selectedParcelCount]);

  useEffect(() => {
    if (creditsResponse && accountSkipTraceCredit.cap < selectedParcelCount) {
      updateValidationError(t("sites.skip_tracing.summary.insufficient_credits"));
    }
  }, [accountSkipTraceCredit, creditsResponse, selectedParcelCount, t]);

  return (
    <div
      className={classNames(
        "bg-background-surface-neutral",
        "flex",
        "flex-col",
        "justify-start",
        "w-full",
        "h-full",
        "p-8",
        "gap-y-8"
      )}
      data-testid="skip-tracing-confirm-order"
    >
      <span className={classNames("flex", "justify-center", "text-2xl", "font-semibold")}>
        {t("sites.skip_tracing.confirm_and_process")}
      </span>
      <div className={classNames("flex", "justify-center", "w-full")}>
        <div
          className={classNames(
            "flex",
            "flex-col",
            "w-[329px]",
            "bg-background-surface",
            "border",
            "border-border-action",
            "rounded-lg",
            "p-4"
          )}
        >
          <span className={classNames("flex", "text-base", "font-semibold", "pb-4")}>
            {t("sites.skip_tracing.summary.skip_tracing_summary")}
          </span>
          <div className={classNames("flex", "justify-between", "pb-3", "text-sm")}>
            <span>
              {selectedParcelCount}x {t("sites.skip_tracing.summary.skip_trace")}
            </span>
            <span>
              {t("sites.skip_tracing.summary.nr_of_credits", {
                nr_of_credits: selectedParcelCount * -1,
              })}
            </span>
          </div>
          <div
            className={classNames(
              "flex",
              "flex-col",
              "border-y",
              "border-border-action",
              "py-3",
              "text-text-base-secondary"
            )}
          >
            <div className={classNames("flex", "flex-row", "justify-between", "text-sm")}>
              <span>{t("sites.skip_tracing.summary.skip_trace_credits")}</span>

              <Tooltip.Wrapper>
                <Tooltip.Trigger>
                  <InfoIcon />
                </Tooltip.Trigger>
                <Tooltip.Content align="center" side="bottom" theme={Theme.Dark} size="sm">
                  <p className="pt-2">Cap: {accountSkipTraceCredit.cap}</p>
                  <p>Used credits: {accountSkipTraceCredit.used}</p>
                  <p>Required: {selectedParcelCount}</p>
                </Tooltip.Content>
              </Tooltip.Wrapper>
            </div>
            <span className="text-xs">
              {t("sites.skip_tracing.summary.remaining_credits_left_after_purchase", {
                nr_of_credits: remainingCredits,
              })}
            </span>
          </div>
          <div className={classNames("flex", "justify-between", "pt-3", "text-base", "font-semibold")}>
            <span>{t("sites.skip_tracing.summary.total_credits")}</span>
            <span className="text-background-surface-success-accent">
              {t("sites.skip_tracing.summary.nr_of_credits", {
                nr_of_credits: selectedParcelCount,
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
      <g clipPath="url(#clip0_3198_634)">
        <path
          fill="#1F2937"
          d="M8 14.667A6.667 6.667 0 118 1.334a6.667 6.667 0 010 13.333zm0-1.334A5.333 5.333 0 108 2.667a5.333 5.333 0 000 10.666zm-.666-8.666h1.333V6H7.334V4.667zm0 2.666h1.333v4H7.334v-4z"
        ></path>
      </g>
      <defs>
        <clipPath id="clip0_3198_634">
          <path fill="#fff" d="M0 0H16V16H0z"></path>
        </clipPath>
      </defs>
    </svg>
  );
};
